import { useTranslation } from 'react-i18next';

import { FertWorkDetailRecord } from '@/app/services/fertWork';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type ViewDonorModalProps = {
  visible: boolean;
  donorRecord: FertWorkDetailRecord | null;
  onHide: () => void;
};

const LabelValue = ({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) => {
  const displayValue =
    value === null || value === undefined || value === ''
      ? '-'
      : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : value;

  return (
    <div className="flex flex-col">
      <label className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
        {label}
      </label>
      <div className="mt-0.5 text-sm text-gray-900">{displayValue}</div>
    </div>
  );
};

export const ViewDonorModal = ({
  visible,
  donorRecord,
  onHide,
}: ViewDonorModalProps) => {
  const { t } = useTranslation();
  const formatMatTime = (matTime?: string) => {
    if (!matTime) return '-';
    const date = new Date(matTime);
    if (isNaN(date.getTime())) return '-';
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${datePart}, ${hours}:${minutes}`;
  };

  if (!donorRecord) return null;

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('fertWork.donorRecordDetails', 'Donor Record Details')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="grid grid-cols-3 gap-x-6 gap-y-3">
          <LabelValue
            label={t('fertWork.opuSite', 'OPU Site')}
            value={donorRecord.opuSiteName}
          />
          <LabelValue
            label={t('fertWork.matTime', 'Mat Time')}
            value={formatMatTime(donorRecord.matTime)}
          />
          <LabelValue
            label={t('fertWork.totalOocytes', 'Total Oocytes')}
            value={donorRecord.totalOocytes}
          />

          <LabelValue
            label={t('fertWork.donorName', 'Donor Name')}
            value={donorRecord.donorName}
          />
          <LabelValue
            label={t('fertWork.donorLocation', 'Donor Location')}
            value={donorRecord.donorLocation}
          />
          <LabelValue
            label={t('fertWork.donorBreed', 'Donor Breed')}
            value={donorRecord.donorBreed}
          />

          <LabelValue
            label={t('fertWork.sireName', 'Sire Name')}
            value={donorRecord.sireName}
          />
          <LabelValue
            label={t('fertWork.sireBreed', 'Sire Breed')}
            value={donorRecord.sireBreed}
          />
          <LabelValue
            label={t('fertWork.semenType', 'Semen Type')}
            value={donorRecord.semenType}
          />

          <div className="col-span-3">
            <LabelValue
              label={t('fertWork.semenComments', 'Semen Comments')}
              value={donorRecord.semenComments}
            />
          </div>

          <LabelValue
            label={t('fertWork.pelletNumber', 'Pellet Number')}
            value={donorRecord.pelletNumber}
          />
          <LabelValue
            label={t('fertWork.semenUnits', 'Unit #')}
            value={donorRecord.semenUnits ?? 1}
          />
          <LabelValue
            label={t('fertWork.accountName', 'Account Name')}
            value={donorRecord.accountName}
          />
          <LabelValue
            label={t('fertWork.shipLabel', 'Ship Label')}
            value={donorRecord.shipLabel}
          />

          <LabelValue
            label={t('fertWork.splitRatio', 'Split Ratio')}
            value={donorRecord.splitRatio}
          />
          <LabelValue
            label={t('fertWork.isSplit', 'Is Split')}
            value={donorRecord.isSplit}
          />
          <LabelValue
            label={t('fertWork.separateFert', 'Separate Fert.')}
            value={donorRecord.separateFert}
          />

          <LabelValue
            label={t('fertWork.hasRevision', 'Has Revision')}
            value={donorRecord.hasRevisionAvailable}
          />
          <LabelValue
            label={t('fertWork.hasIssues', 'Has Issues')}
            value={donorRecord.hasValidationIssues}
          />
          <LabelValue
            label={t('fertWork.issueCountLabel', 'Issue Count')}
            value={donorRecord.issueCount}
          />

          {(donorRecord.revision || donorRecord.revisionComment) && (
            <div className="col-span-3 grid grid-cols-3 gap-x-6 gap-y-3 border-t border-gray-200 pt-2">
              <LabelValue
                label={t('fertWork.revision', 'Revision')}
                value={donorRecord.revision}
              />
              <div className="col-span-2">
                <LabelValue
                  label={t('fertWork.revisionComment', 'Revision Comment')}
                  value={donorRecord.revisionComment}
                />
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onHide}>{t('common.close', 'Close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
